
import { useState } from "react";
import { CloseIcon } from "./icons";
import { trackClick } from "../utils/analytics";

interface Researcher {
  name: string;
  url?: string;
}

interface TechCardProps {
  /** A % (number), a status line (string), or null / '' for no badge. */
  relevanceScore: number | string | null;
  title: string;
  institution: string;
  location: string;
  trl: string;
  trlReasoning?: string; // Add TRL reasoning prop
  researchers: Researcher[];
  summary: string;
  ipSummary?: string;
  publicationsSummary?: string;
  activeTab?: "Summary" | "IP Highlights" | "Publications";
  onContact: () => void;
  onClose: () => void;
  contactButtonText?: string; // Customizable contact button text
  contactDisabled?: boolean; // Whether contact button is disabled
  profileIncomplete?: boolean; // Whether user profile is incomplete
  /** Contacting is a paid feature and this account has not paid. */
  contactLocked?: boolean;
  /** Raise the upgrade modal instead of contacting. */
  onLockedContact?: () => void;
  // API callback props
  onBookmarkToggle?: (isBookmarked: boolean) => void;
  onLikeDislike?: (liked: boolean | null) => void;
  initialBookmarked?: boolean;
  initialLiked?: boolean | null;
}

export default function TechCard({
  relevanceScore,
  title,
  institution,
  location,
  trl,
  trlReasoning,
  researchers,
  summary,
  ipSummary,
  publicationsSummary,
  activeTab = "Summary",
  onContact,
  onClose,
  contactButtonText = "Contact university",
  contactDisabled = false,
  profileIncomplete = false,
  contactLocked = false,
  onLockedContact,
  onBookmarkToggle,
  onLikeDislike,
  initialBookmarked = false,
  initialLiked = null,
}: TechCardProps) {
  const [trlExpanded, _setTrlExpanded] = useState(false);
  const [researchersExpanded, _setResearchersExpanded] = useState(true);
  const [currentTab, _setCurrentTab] = useState(activeTab);

  const setTrlExpanded = (v: boolean) => { trackClick('tech_modal_click_trl_dropdown'); _setTrlExpanded(v); };
  const setResearchersExpanded = (v: boolean) => { trackClick('tech_modal_click_researchers'); _setResearchersExpanded(v); };
  const setCurrentTab = (tab: typeof activeTab) => { trackClick('tech_modal_click_tab', { tab }); _setCurrentTab(tab); };
  const [isBookmarked, setIsBookmarked] = useState(initialBookmarked);
  const [feedback, setFeedback] = useState<"like" | "dislike" | null>(
    initialLiked === true ? "like" : initialLiked === false ? "dislike" : null
  );
  // No badge without a score (a card opened without a ranking, or a
  // filter-only search) rather than an empty pill or a made-up number.
  const hasBadge = typeof relevanceScore === 'number'
    ? isFinite(relevanceScore)
    : relevanceScore !== null && relevanceScore !== undefined && String(relevanceScore).trim() !== '';

  const handleBookmarkClick = () => {
    trackClick('tech_modal_click_bookmark', { title });
    const newBookmarked = !isBookmarked;
    setIsBookmarked(newBookmarked);
    onBookmarkToggle?.(newBookmarked);
  };

  const handleLikeClick = () => {
    trackClick('tech_modal_click_thumbs_up', { title });
    const newFeedback = feedback === "like" ? null : "like";
    setFeedback(newFeedback);
    onLikeDislike?.(newFeedback === "like" ? true : null);
  };

  const handleDislikeClick = () => {
    trackClick('tech_modal_click_thumbs_down', { title });
    const newFeedback = feedback === "dislike" ? null : "dislike";
    setFeedback(newFeedback);
    onLikeDislike?.(newFeedback === "dislike" ? false : null);
  };

  return (
    <>
      {/* Desktop Version */}
      <div className="hidden sm:block utech-nocopy">
        <div className="w-full max-w-[434px] mx-auto">
          <div className="flex flex-col">
            <div className="relative w-[434px] h-[516px] flex flex-col items-center p-3 rounded-xl">
              {/* Background SVG */}
              <svg
                width="434"
                height="508"
                viewBox="0 0 434 508"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="absolute inset-0 w-full h-[508px]"
              >
                <path
                  d="M16.0029 0.5H120.016C129.661 0.500059 138.765 4.96144 144.675 12.585C150.774 20.4528 160.169 25.0576 170.124 25.0576H418C426.56 25.0576 433.5 31.9972 433.5 40.5576V492C433.5 500.56 426.56 507.5 418 507.5H299.713C289.691 507.5 280.278 502.69 274.405 494.568C265.519 482.278 251.274 475 236.107 475H16.0918C7.53274 475 0.593513 468.062 0.591797 459.503L0.50293 16.0029C0.501307 7.44147 7.44149 0.500151 16.0029 0.5Z"
                  fill="white"
                />
                <path
                  d="M16.0029 0.5H120.016C129.661 0.500059 138.765 4.96144 144.675 12.585C150.774 20.4528 160.169 25.0576 170.124 25.0576H418C426.56 25.0576 433.5 31.9972 433.5 40.5576V492C433.5 500.56 426.56 507.5 418 507.5H299.713C289.691 507.5 280.278 502.69 274.405 494.568C265.519 482.278 251.274 475 236.107 475H16.0918C7.53274 475 0.593513 468.062 0.591797 459.503L0.50293 16.0029C0.501307 7.44147 7.44149 0.500151 16.0029 0.5Z"
                  fill="#F0EAE1"
                  fillOpacity="0.3"
                />
                <path
                  d="M16.0029 0.5H120.016C129.661 0.500059 138.765 4.96144 144.675 12.585C150.774 20.4528 160.169 25.0576 170.124 25.0576H418C426.56 25.0576 433.5 31.9972 433.5 40.5576V492C433.5 500.56 426.56 507.5 418 507.5H299.713C289.691 507.5 280.278 502.69 274.405 494.568C265.519 482.278 251.274 475 236.107 475H16.0918C7.53274 475 0.593513 468.062 0.591797 459.503L0.50293 16.0029C0.501307 7.44147 7.44149 0.500151 16.0029 0.5Z"
                  stroke="#F0EAE1"
                  strokeWidth="1"
                />
              </svg>

              {/* Content positioned relative to the background */}
              <div className="relative z-10 w-full flex flex-col gap-3 pr-3">
                {/* Relevance Score Badge (none without a score) */}
                {hasBadge && (
                <div className="flex items-center gap-[2.79px] self-stretch">
                  <div className="flex items-center justify-center gap-[10px] px-2 py-1 bg-primary-100 rounded-[56px]">
                    <span
                      className="text-[11px] font-normal text-primary-og-600"
                      style={{ fontFamily: "Space Grotesk" }}
                    >
                      {typeof relevanceScore === 'number' ? `${relevanceScore}% relevance` : relevanceScore}
                    </span>
                  </div>
                </div>
                )}

                {/* Title and Location */}
                <div className="flex flex-col items-start gap-2 self-stretch">
                  <h2
                    className="text-base font-medium text-grayscale-1300 leading-normal self-stretch"
                    style={{ fontFamily: "Space Grotesk" }}
                  >
                    {title}
                  </h2>
                  <div className="flex items-center gap-[11.159px] self-stretch">
                    <div className="flex items-center gap-[0.93px]">
                      <div className="flex w-4 h-4 flex-col justify-center items-center aspect-square">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-4 h-4 flex-shrink-0"
                        >
                          <path
                            d="M2.99991 6.49994C2.99991 10.9999 7.99991 14.4999 7.99991 14.4999C7.99991 14.4999 12.9999 10.9999 12.9999 6.49994C12.9999 5.17386 12.4731 3.90209 11.5354 2.96441C10.5978 2.02672 9.32599 1.49994 7.99991 1.49994C6.67383 1.49994 5.40206 2.02672 4.46437 2.9644C3.52669 3.90209 2.99991 5.17386 2.99991 6.49994Z"
                            fill="#9A7A4C"
                          />
                          <path
                            d="M8.00003 8.50003C6.89546 8.50003 6.00003 7.6046 6.00003 6.50003C6.00003 5.39546 6.89546 4.50003 8.00003 4.50003C9.1046 4.50003 10 5.39546 10 6.50003C10 7.6046 9.1046 8.50003 8.00003 8.50003Z"
                            stroke="white"
                            strokeWidth="0.929883"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <span
                        className="text-[11px] font-normal text-grayscale-1000"
                        style={{ fontFamily: "Space Grotesk" }}
                      >
                        {[institution, location].filter(Boolean).join(', ')}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Divider */}
                <div className="w-[410px] h-[0.5px] bg-primary-300"></div>

                {/* TRL Section */}
                <div className="flex flex-col justify-center items-start self-stretch">
                  <div className="flex justify-between items-center self-stretch py-[2px]">
                    <span
                      className="text-xs font-medium text-grayscale-1300"
                      style={{
                        fontFamily: "Space Grotesk",
                        lineHeight: "7.439px",
                      }}
                    >
                      {trl}
                    </span>
                    <button
                      onClick={() => setTrlExpanded(!trlExpanded)}
                      className="flex w-4 h-4 flex-col justify-center items-center aspect-square"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-4 h-4 flex-shrink-0"
                      >
                        <path
                          d={
                            trlExpanded
                              ? "M2.99991 10L7.99991 5L12.9999 10"
                              : "M2.99991 6L7.99991 11L12.9999 6"
                          }
                          stroke="#141414"
                          strokeWidth="0.929883"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                  {trlExpanded && (
                    <div className="w-full mt-2 space-y-1 max-h-[35px] overflow-y-auto">
                      <div
                        className="text-[11px] font-normal text-grayscale-1000"
                        style={{ fontFamily: "Space Grotesk", lineHeight: "16px" }}
                      >
                        {trl.toLowerCase().includes('not disclosed') 
                          ? 'TRL not disclosed. Please contact the university for more details.'
                          : (trlReasoning || `Technology Readiness Level ${trl.replace('TRL ', '')}: Technology validated in relevant environment`)}
                      </div>
                    </div>
                  )}
                </div>

                {/* Divider */}
                <div className="w-[410px] h-[0.5px] bg-primary-300"></div>

                {/* Researchers Section */}
                <div className="flex flex-col items-start gap-1 self-stretch">
                  <div className="flex justify-between items-center self-stretch py-[2px]">
                    <span
                      className="text-xs font-medium text-grayscale-1300"
                      style={{
                        fontFamily: "Space Grotesk",
                        lineHeight: "7.439px",
                      }}
                    >
                      Researchers
                    </span>
                    <button
                      onClick={() =>
                        setResearchersExpanded(!researchersExpanded)
                      }
                      className="flex w-4 h-4 flex-col justify-center items-center aspect-square"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-4 h-4 flex-shrink-0"
                      >
                        <path
                          d={
                            researchersExpanded
                              ? "M2.99991 10L7.99991 5L12.9999 10"
                              : "M2.99991 6L7.99991 11L12.9999 6"
                          }
                          stroke="#141414"
                          strokeWidth="0.929883"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                  {researchersExpanded && (
                    <div className="flex items-start gap-1 flex-wrap max-h-[35px] overflow-y-auto">
                      {researchers.map((researcher, index) => {
                        const isSingleResearcher = researchers.length === 1;
                        return (
                          <span
                            key={index}
                            className={
                              isSingleResearcher
                                ? "text-[11px] font-medium text-grayscale-1000"
                                : "text-[11px] font-medium text-grayscale-1000 truncate max-w-[150px]"
                            }
                            style={{ fontFamily: "Space Grotesk" }}
                            title={researcher.name}
                          >
                            {researcher.name}
                            {index < researchers.length - 1 ? "," : ""}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Divider */}
                <div className="w-[410px] h-[0.5px] bg-primary-300"></div>

                {/* Tabs and Content */}
                <div className="flex flex-col items-start gap-2 self-stretch">
                  <div className="flex items-start p-1 bg-[rgba(128,101,63,0.12)] rounded-2xl relative self-start">
                    {/* Active tab background */}
                    <div
                      className="absolute h-[22px] bg-white rounded-2xl shadow-custom-light flex flex-col justify-center items-center"
                      style={{
                        left: "4px",
                        top: "4px",
                        width:
                          currentTab === "Summary"
                            ? "76px"
                            : currentTab === "IP Highlights"
                            ? "92px"
                            : "88px",
                        transform:
                          currentTab === "Summary"
                            ? "translateX(0px)"
                            : currentTab === "IP Highlights"
                            ? "translateX(76px)"
                            : "translateX(168px)",
                        transition:
                          "transform 0.2s ease-out, width 0.2s ease-out",
                      }}
                    />

                    {/* Tab buttons */}
                    <button
                      onClick={() => setCurrentTab("Summary")}
                      className="flex w-[76px] p-1 flex-col justify-center items-center relative z-10"
                    >
                      <span
                        className={`text-[11px] font-medium whitespace-nowrap ${
                          currentTab === "Summary"
                            ? "text-primary-og-600"
                            : "text-grayscale-1300"
                        }`}
                        style={{
                          fontFamily:
                            currentTab === "Summary"
                              ? "Space Grotesk"
                              : "Inter",
                        }}
                      >
                        Summary
                      </span>
                    </button>
                    <button
                      onClick={() => setCurrentTab("IP Highlights")}
                      className="flex w-[92px] p-1 flex-col justify-center items-center relative z-10"
                    >
                      <span
                        className={`text-[11px] font-normal whitespace-nowrap ${
                          currentTab === "IP Highlights"
                            ? "text-primary-og-600"
                            : "text-grayscale-1300"
                        }`}
                        style={{ fontFamily: "Space Grotesk" }}
                      >
                        IP Highlights
                      </span>
                    </button>
                    <button
                      onClick={() => setCurrentTab("Publications")}
                      className="flex w-[88px] p-1 flex-col justify-center items-center relative z-10"
                    >
                      <span
                        className={`text-[11px] font-normal whitespace-nowrap ${
                          currentTab === "Publications"
                            ? "text-primary-og-600"
                            : "text-grayscale-1300"
                        }`}
                        style={{ fontFamily: "Space Grotesk" }}
                      >
                        Publications
                      </span>
                    </button>
                  </div>

                  {/* Content */}
                  <div
                    className={`text-xs font-normal text-grayscale-1000 self-stretch ${(() => {
                      const content = currentTab === "Summary" 
                        ? summary 
                        : currentTab === "IP Highlights" 
                        ? (ipSummary || "No IP summary available")
                        : (publicationsSummary || "No publications summary available");
                      
                      // Count how many expanders are opened
                      const expandersOpen = (trlExpanded ? 1 : 0) + (researchersExpanded ? 1 : 0);
                      
                      // Enable scroll if content exceeds 450 chars OR if any expander is opened
                      if (content.length > 450 || trlExpanded || researchersExpanded) {
                        // Set max-height based on number of expanders opened
                        if (expandersOpen === 2) {
                          return 'max-h-[100px] overflow-y-auto';
                        } else if (expandersOpen === 1) {
                          return 'max-h-[145px] overflow-y-auto';
                        } else {
                          // No expanders open, but content > 450 chars
                          return 'max-h-[180px] overflow-y-auto';
                        }
                      }
                      return '';
                    })()}`}
                    style={{ fontFamily: "Space Grotesk", lineHeight: "18px" }}
                  >
                    {currentTab === "Summary" && summary}
                    {currentTab === "IP Highlights" && (ipSummary || "No IP summary available")}
                    {currentTab === "Publications" && (publicationsSummary || "No publications summary available")}
                  </div>
                </div>
              </div>
               {/* Action Buttons */}
                <div
                    className="flex w-[410px] justify-end items-center gap-[2.79px] absolute"
                    style={{ left: "12px", bottom: "20px" }}
                >
                    <div className="flex justify-end items-end gap-3">
                    <button
                        onClick={handleLikeClick}
                        className="flex p-2 justify-center items-center gap-2 bg-primary-100 rounded-[32px]">
                        <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-4 h-4"
                        >
                        <path
                            d="M13.5929 12.9705C13.4428 13.9461 12.6033 14.6663 11.6162 14.6663H2.66536C1.92898 14.6663 1.33203 14.0694 1.33203 13.333V8.66634C1.33203 7.92996 1.92898 7.33301 2.66536 7.33301H4.23212C4.49557 7.33301 4.73432 7.17785 4.84132 6.9371L7.1874 1.65842C7.27535 1.46054 7.47159 1.33301 7.68814 1.33301C8.59604 1.33301 9.33203 2.069 9.33203 2.9769V5.33301C9.33203 5.7012 9.63051 5.99967 9.9987 5.99967H12.3341C13.5599 5.99967 14.4973 7.09227 14.3109 8.30379L13.5929 12.9705Z"
                            fill={feedback === 'like' ? '#9A7A4C' : 'none'}
                            stroke="#9A7A4C"
                        />
                        </svg>
                    </button>
                    <button
                        onClick={handleDislikeClick}
                        className="flex p-2 justify-center items-center gap-2 bg-primary-100 rounded-[32px]">
                        <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-4 h-4"
                        >
                        <path
                            d="M11.3346 1.33366V8.66699M14.668 7.33366V2.66699C14.668 1.93061 14.071 1.33366 13.3346 1.33366H4.38381C3.39666 1.33366 2.55717 2.05388 2.40706 3.02954L1.68911 7.69621C1.50273 8.90773 2.44009 10.0003 3.66586 10.0003H6.0013C6.36949 10.0003 6.66797 10.2988 6.66797 10.667V13.0231C6.66797 13.931 7.40396 14.667 8.31186 14.667C8.52841 14.667 8.72465 14.5395 8.8126 14.3416L11.1587 9.0629C11.2657 8.82215 11.5044 8.66699 11.7679 8.66699H13.3346C14.071 8.66699 14.668 8.07004 14.668 7.33366Z"
                            fill={feedback === 'dislike' ? '#9A7A4C' : 'none'}
                            stroke="#9A7A4C"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        </svg>
                    </button>
                    <button
                        onClick={handleBookmarkClick}
                        className="flex p-2 justify-center items-center gap-2 bg-primary-100 rounded-[32px]">
                        <div className="flex w-4 h-4 justify-center items-center">
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-4 h-4 flex-shrink-0"
                        >
                            <path
                            d="M12 14L8 11.5L4 14V3C4 2.86739 4.05268 2.74021 4.14645 2.64645C4.24021 2.55268 4.36739 2.5 4.5 2.5H11.5C11.6326 2.5 11.7598 2.55268 11.8536 2.64645C11.9473 2.74021 12 2.86739 12 3V14Z"
                            fill={isBookmarked ? '#80653F' : 'none'}
                            stroke="#80653F"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            />
                        </svg>
                        </div>
                    </button>
                    </div>
                </div>
            </div>
            {/* Three states, in priority order: an incomplete profile blocks
                outright (we would be introducing a stranger), a locked account
                stays CLICKABLE and opens the upgrade modal — a dead grey
                button teaches nothing — and otherwise it contacts. */}
            <button 
              onClick={
                profileIncomplete ? undefined
                : contactLocked ? () => { trackClick('tech_modal_click_contact_locked'); onLockedContact?.(); }
                : () => { trackClick('tech_modal_click_contact'); onContact(); }
              } 
              disabled={profileIncomplete}
              title={
                profileIncomplete ? 'Please complete your profile to contact universities'
                : contactLocked ? 'Upgrade to see the university contact details'
                // Native tooltip carries the full meaning that no longer fits
                // on the button itself.
                : 'Get the university contact details, or ask our team '
                  + 'to reach out to them for you'
              }
              className={`flex w-[260px] justify-between items-center px-4 py-[7px] rounded-[32px] relative -mt-[33px] transition-colors ${
                profileIncomplete
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  : contactLocked
                    // Greyed but still clickable — the click is what opens the
                    // upgrade prompt that explains why it is locked.
                    ? 'bg-gray-200 text-gray-500'
                    : 'bg-primary-200 hover:bg-primary-300'
              }`}
            >
                <div className="flex items-center gap-2">
                    <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-4 h-4"
                    >
                    <path
                        d="M14 3.5L8 9L2 3.5"
                        stroke="#525252"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                    <path
                        d="M2 3.5H14V12C14 12.1326 13.9473 12.2598 13.8536 12.3536C13.7598 12.4473 13.6326 12.5 13.5 12.5H2.5C2.36739 12.5 2.24021 12.4473 2.14645 12.3536C2.05268 12.2598 2 12.1326 2 12V3.5Z"
                        stroke="#525252"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                    <path
                        d="M6.90625 8L2.15625 12.3562"
                        stroke="#525252"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                    <path
                        d="M13.8438 12.3562L9.09375 8"
                        stroke="#525252"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                    </svg>
                    <span
                    // The pill is a fixed w-[260px] and this span cannot shrink
                    // (whitespace-nowrap, no min-w-0), so a longer label
                    // overflows rather than wrapping. "Contact university"
                    // measures ~126px against ~184px available; the reverted
                    // "Contact Tech Transfer Office" was 193px and broke it.
                    className="text-sm font-medium text-grayscale-1000 text-center whitespace-nowrap"
                    style={{
                        fontFamily: "Space Grotesk",
                        fontFeatureSettings: "'liga' off, 'clig' off",
                    }}
                    >
                    {profileIncomplete ? 'Complete Profile to Contact'
                      // Locked shows the SAME action, greyed, so the user learns
                      // what they get rather than a different feature name. We
                      // are not making introductions any more either.
                      : contactLocked ? contactButtonText
                      : contactButtonText}
                    </span>
                    {/* The (i) badge and its tooltip were removed: they existed
                        to explain the abbreviation "TTO", and the label is now
                        the self-explanatory "Contact university". */}
                </div>
                <div className="flex w-4 h-4 justify-center items-center flex-shrink-0">
                    <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-4 h-4 flex-shrink-0"
                    >
                    <path
                        d="M2.5 8H12.5"
                        stroke="#525252"
                        strokeLinecap="square"
                        strokeLinejoin="round"
                    />
                    <path
                        d="M9 3.5L13.5 8L9 12.5"
                        stroke="#525252"
                        strokeLinecap="square"
                    />
                    </svg>
                </div>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Version */}
      <div className="sm:hidden w-full bg-alternate-bg shadow-xl flex flex-col font-grotesk max-h-[90vh] rounded-2xl relative utech-nocopy">
        <button
          onClick={() => { trackClick('tech_modal_close'); onClose(); }}
          className="absolute top-4 right-4 z-20 flex p-2 justify-center items-center gap-2 rounded-full bg-grayscale-1200 shadow-custom-light"
        >
          <CloseIcon />
        </button>

        <div className="p-4 flex flex-col gap-3 overflow-y-auto">
          {/* Top section: score, title, location */}
          {hasBadge && (
          <div className="flex items-center gap-[2.79px] self-stretch">
            <div className="flex items-center justify-center gap-[10px] px-2 py-1 bg-primary-100 rounded-[56px]">
              <span className="text-[11px] font-normal text-primary-og-600 font-grotesk">
                {typeof relevanceScore === 'number' ? `${relevanceScore}% relevance` : relevanceScore}
              </span>
            </div>
          </div>
          )}
          <div className="flex flex-col items-start gap-2 self-stretch">
            <h2 className="text-base font-medium text-grayscale-1300 leading-normal self-stretch font-grotesk">
              {title}
            </h2>
            <div className="flex items-center gap-[11.159px] self-stretch">
              <div className="flex items-center gap-[0.93px]">
                <div className="flex w-4 h-4 flex-col justify-center items-center aspect-square">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 flex-shrink-0">
                    <path d="M2.99991 6.49994C2.99991 10.9999 7.99991 14.4999 7.99991 14.4999C7.99991 14.4999 12.9999 10.9999 12.9999 6.49994C12.9999 5.17386 12.4731 3.90209 11.5354 2.96441C10.5978 2.02672 9.32599 1.49994 7.99991 1.49994C6.67383 1.49994 5.40206 2.02672 4.46437 2.9644C3.52669 3.90209 2.99991 5.17386 2.99991 6.49994Z" fill="#9A7A4C" />
                    <path d="M8.00003 8.50003C6.89546 8.50003 6.00003 7.6046 6.00003 6.50003C6.00003 5.39546 6.89546 4.50003 8.00003 4.50003C9.1046 4.50003 10 5.39546 10 6.50003C10 7.6046 9.1046 8.50003 8.00003 8.50003Z" stroke="white" strokeWidth="0.929883" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <span className="text-[11px] font-normal text-grayscale-1000 font-grotesk">
                  {[institution, location].filter(Boolean).join(', ')}
                </span>
              </div>
            </div>
          </div>

          <div className="w-full h-[0.5px] bg-primary-300"></div>

          {/* TRL Section */}
          <div className="flex flex-col justify-center items-start self-stretch">
            <div className="flex justify-between items-center self-stretch py-[2px]">
              <span className="text-xs font-medium text-grayscale-1300 font-grotesk">
                {trl}
              </span>
              <button onClick={() => setTrlExpanded(!trlExpanded)} className="flex w-4 h-4 flex-col justify-center items-center aspect-square">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 flex-shrink-0">
                  <path d={trlExpanded ? "M2.99991 10L7.99991 5L12.9999 10" : "M2.99991 6L7.99991 11L12.9999 6"} stroke="#141414" strokeWidth="0.929883" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
            {trlExpanded && (
              <div className="w-full mt-2 space-y-1 text-xs text-grayscale-1000 max-h-[35px] overflow-y-auto">
                <div style={{ fontFamily: "Space Grotesk", lineHeight: "16px" }}>
                  {trl.toLowerCase().includes('not disclosed') 
                    ? 'TRL not disclosed. Please contact the university for more details.'
                    : (trlReasoning || `Technology Readiness Level ${trl.replace('TRL ', '')}: Technology validated in relevant environment`)}
                </div>
              </div>
            )}
          </div>

          <div className="w-full h-[0.5px] bg-primary-300"></div>

          {/* Researchers Section */}
          <div className="flex flex-col items-start gap-1 self-stretch">
            <div className="flex justify-between items-center self-stretch py-[2px]">
              <span className="text-xs font-medium text-grayscale-1300 font-grotesk">
                Researchers
              </span>
              <button onClick={() => setResearchersExpanded(!researchersExpanded)} className="flex w-4 h-4 flex-col justify-center items-center aspect-square">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 flex-shrink-0">
                  <path d={researchersExpanded ? "M2.99991 10L7.99991 5L12.9999 10" : "M2.99991 6L7.99991 11L12.9999 6"} stroke="#141414" strokeWidth="0.929883" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
            {researchersExpanded && (
              <div className="flex flex-wrap items-start gap-1 max-h-[35px] overflow-y-auto">
                {researchers.map((researcher, index) => {
                  const isSingleResearcher = researchers.length === 1;
                  return (
                    <span 
                      key={index} 
                      className={
                        isSingleResearcher
                          ? "text-[11px] font-medium text-grayscale-1000"
                          : "text-[11px] font-medium text-grayscale-1000 truncate max-w-[150px]"
                      }
                      title={researcher.name}
                    >
                      {researcher.name}
                      {index < researchers.length - 1 ? ", " : ""}
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          <div className="w-full h-[0.5px] bg-primary-300"></div>

          {/* Tabs and Content */}
          <div className="flex flex-col items-start gap-2 self-stretch">
            <div className="flex items-start p-1 bg-[rgba(128,101,63,0.12)] rounded-2xl relative self-start">
              <div
                className="absolute h-[22px] bg-white rounded-2xl shadow-custom-light flex flex-col justify-center items-center"
                style={{
                  left: "4px",
                  top: "4px",
                  width: currentTab === "Summary" ? "76px" : currentTab === "IP Highlights" ? "92px" : "88px",
                  transform: currentTab === "Summary" ? "translateX(0px)" : currentTab === "IP Highlights" ? "translateX(76px)" : "translateX(168px)",
                  transition: "transform 0.2s ease-out, width 0.2s ease-out",
                }}
              />
              <button onClick={() => setCurrentTab("Summary")} className="flex w-[76px] p-1 flex-col justify-center items-center relative z-10">
                <span className={`text-[11px] font-medium whitespace-nowrap ${currentTab === "Summary" ? "text-primary-og-600 font-grotesk" : "text-grayscale-1300"}`}>
                  Summary
                </span>
              </button>
              <button onClick={() => setCurrentTab("IP Highlights")} className="flex w-[92px] p-1 flex-col justify-center items-center relative z-10">
                <span className={`text-[11px] whitespace-nowrap ${currentTab === "IP Highlights" ? "text-primary-og-600 font-grotesk font-medium" : "text-grayscale-1300 font-normal"}`}>
                  IP Highlights
                </span>
              </button>
              <button onClick={() => setCurrentTab("Publications")} className="flex w-[88px] p-1 flex-col justify-center items-center relative z-10">
                <span className={`text-[11px] whitespace-nowrap ${currentTab === "Publications" ? "text-primary-og-600 font-grotesk font-medium" : "text-grayscale-1300 font-normal"}`}>
                  Publications
                </span>
              </button>
            </div>
            {/* Content */}
            <div className={`text-xs font-normal text-grayscale-1000 self-stretch ${(() => {
              const content = currentTab === "Summary" 
                ? summary 
                : currentTab === "IP Highlights" 
                ? (ipSummary || "No IP summary available")
                : (publicationsSummary || "No publications summary available");
              
              // Count how many expanders are opened
              const expandersOpen = (trlExpanded ? 1 : 0) + (researchersExpanded ? 1 : 0);
              
              // Enable scroll if content exceeds 450 chars OR if any expander is opened
              if (content.length > 450 || trlExpanded || researchersExpanded) {
                // Set max-height based on number of expanders opened
                if (expandersOpen === 2) {
                  return 'max-h-[140px] overflow-y-auto';
                } else if (expandersOpen === 1) {
                  return 'max-h-[160px] overflow-y-auto';
                } else {
                  // No expanders open, but content > 450 chars
                  return 'max-h-[180px] overflow-y-auto';
                }
              }
              return '';
            })()}`} style={{ fontFamily: "Space Grotesk", lineHeight: "18px" }}>
              {currentTab === "Summary" && summary}
              {currentTab === "IP Highlights" && (ipSummary || "No IP summary available")}
              {currentTab === "Publications" && (publicationsSummary || "No publications summary available")}
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-gray-200">
          <div className="flex justify-between items-center w-full gap-3">
            <button
              onClick={
                profileIncomplete ? () => alert('Please complete your profile to contact universities')
                : contactLocked ? () => { trackClick('tech_modal_click_contact_locked'); onLockedContact?.(); }
                : () => { trackClick('tech_modal_click_contact'); onContact(); }
              }
              disabled={profileIncomplete}
              className={`flex-1 flex justify-between items-center px-3 py-2 rounded-full transition-colors ${
                profileIncomplete 
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed' 
                  : 'bg-primary-200 hover:bg-primary-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-4 h-4"
                >
                  <path d="M14 3.5L8 9L2 3.5" stroke="#525252" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M2 3.5H14V12C14 12.1326 13.9473 12.2598 13.8536 12.3536C13.7598 12.4473 13.6326 12.5 13.5 12.5H2.5C2.36739 12.5 2.24021 12.4473 2.14645 12.3536C2.05268 12.2598 2 12.1326 2 12V3.5Z" stroke="#525252" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M6.90625 8L2.15625 12.3562" stroke="#525252" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M13.8438 12.3562L9.09375 8" stroke="#525252" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span className="text-xs font-medium text-grayscale-1000 text-center font-grotesk whitespace-nowrap">
                  {profileIncomplete ? 'Complete Profile to Contact'
                      // Locked shows the SAME action, greyed, so the user learns
                      // what they get rather than a different feature name. We
                      // are not making introductions any more either.
                      : contactLocked ? contactButtonText
                      : contactButtonText}
                </span>
              </div>
              <div className="flex w-4 h-4 justify-center items-center flex-shrink-0">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-4 h-4 flex-shrink-0"
                >
                  <path d="M2.5 8H12.5" stroke="#525252" strokeLinecap="square" strokeLinejoin="round" />
                  <path d="M9 3.5L13.5 8L9 12.5" stroke="#525252" strokeLinecap="square" />
                </svg>
              </div>
            </button>
            <div className="flex items-center gap-2">
              <button
                onClick={handleLikeClick}
                className="flex p-2 justify-center items-center gap-2 bg-primary-100 rounded-full"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-4 h-4"
                >
                  <path
                    d="M13.5929 12.9705C13.4428 13.9461 12.6033 14.6663 11.6162 14.6663H2.66536C1.92898 14.6663 1.33203 14.0694 1.33203 13.333V8.66634C1.33203 7.92996 1.92898 7.33301 2.66536 7.33301H4.23212C4.49557 7.33301 4.73432 7.17785 4.84132 6.9371L7.1874 1.65842C7.27535 1.46054 7.47159 1.33301 7.68814 1.33301C8.59604 1.33301 9.33203 2.069 9.33203 2.9769V5.33301C9.33203 5.7012 9.63051 5.99967 9.9987 5.99967H12.3341C13.5599 5.99967 14.4973 7.09227 14.3109 8.30379L13.5929 12.9705Z"
                    fill={feedback === "like" ? "#9A7A4C" : "none"}
                    stroke="#9A7A4C"
                  />
                </svg>
              </button>
              <button
                onClick={handleDislikeClick}
                className="flex p-2 justify-center items-center gap-2 bg-primary-100 rounded-full"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-4 h-4"
                >
                  <path
                    d="M11.3346 1.33366V8.66699M14.668 7.33366V2.66699C14.668 1.93061 14.071 1.33366 13.3346 1.33366H4.38381C3.39666 1.33366 2.55717 2.05388 2.40706 3.02954L1.68911 7.69621C1.50273 8.90773 2.44009 10.0003 3.66586 10.0003H6.0013C6.36949 10.0003 6.66797 10.2988 6.66797 10.667V13.0231C6.66797 13.931 7.40396 14.667 8.31186 14.667C8.52841 14.667 8.72465 14.5395 8.8126 14.3416L11.1587 9.0629C11.2657 8.82215 11.5044 8.66699 11.7679 8.66699H13.3346C14.071 8.66699 14.668 8.07004 14.668 7.33366Z"
                    fill={feedback === "dislike" ? "#9A7A4C" : "none"}
                    stroke="#9A7A4C"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <button
                onClick={handleBookmarkClick}
                className="flex p-2 justify-center items-center gap-2 bg-primary-100 rounded-full"
              >
                <div className="flex w-4 h-4 justify-center items-center">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-4 h-4 flex-shrink-0"
                  >
                    <path
                      d="M12 14L8 11.5L4 14V3C4 2.86739 4.05268 2.74021 4.14645 2.64645C4.24021 2.55268 4.36739 2.5 4.5 2.5H11.5C11.6326 2.5 11.7598 2.55268 11.8536 2.64645C11.9473 2.74021 12 2.86739 12 3V14Z"
                      fill={isBookmarked ? "#80653F" : "none"}
                      stroke="#80653F"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
